/**
 * ActivityFlusher — single-mount driver that drains the activity
 * outbox to the runtime ingest endpoint.
 *
 * Flush triggers (whichever fires first):
 *   - 5 s debounce after the last enqueue.
 *   - 100 events queued.
 *   - `pagehide` / `beforeunload` (uses `sendBeacon` + `fetch
 *     keepalive` fallback).
 *   - `online` event (recovers from offline gaps).
 *
 * Backoff on POST failure: 1/2/4/8/30 s capped. The store keeps
 * unacked events so the next flush retries them.
 *
 * Mirror of [`<FeedbackOutboxRunner>`](../feedback/FeedbackOutboxRunner.tsx)
 * but with finer-grained triggers because activity emission rates
 * are higher than feedback.
 */

import { useEffect, useRef } from "react";

import {
  flushOutbox,
  MAX_BATCH_EVENTS,
  sendBeaconBatch,
} from "@/lib/activity/activity-client";
import { getToken } from "@/lib/lexserver-client";
import { useActivityStore } from "@/stores/useActivityStore";

const DEBOUNCE_MS = 5_000;
const BATCH_THRESHOLD = 100;
const BACKOFF_MS = [1_000, 2_000, 4_000, 8_000, 30_000] as const;

export function ActivityFlusher(): null {
  // Pull only the slice we read in the effect — no re-renders on
  // outbox changes (we still re-run the subscription below).
  const enqueueCount = useActivityStore((s) => s.outbox.length);
  const isFlushing = useActivityStore((s) => s.isFlushing);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const failedAttemptsRef = useRef(0);
  const inFlightRef = useRef(false);
  const cachedTokenRef = useRef<string | undefined>(undefined);

  // Pre-fetch + cache the token once so `pagehide` (synchronous) has
  // it without an async hop. Refreshed on auth-state change is out
  // of scope for L7 — sendBeacon falls back to no-auth which the
  // server rejects with 401 (acceptable for the "user closes tab
  // mid-session" edge).
  useEffect(() => {
    void getToken()
      .then((t) => {
        cachedTokenRef.current = t;
      })
      .catch(() => {
        /* dev mode without signing key — token undefined */
      });
  }, []);

  // Pagehide / beforeunload → synchronous flush via sendBeacon.
  useEffect(() => {
    const onPageLeave = () => {
      const events = useActivityStore.getState().outbox;
      if (events.length === 0) return;
      const beaconed = sendBeaconBatch(
        events.slice(0, MAX_BATCH_EVENTS),
        cachedTokenRef.current,
      );
      if (beaconed) {
        // Optimistic ack — we don't get a response. The server is
        // idempotent on `id` so a retry on next page load is
        // harmless if the beacon never landed.
        useActivityStore
          .getState()
          .ack(events.slice(0, MAX_BATCH_EVENTS).map((e) => e.id));
      }
    };
    window.addEventListener("pagehide", onPageLeave);
    window.addEventListener("beforeunload", onPageLeave);
    return () => {
      window.removeEventListener("pagehide", onPageLeave);
      window.removeEventListener("beforeunload", onPageLeave);
    };
  }, []);

  // Drain on `online` — recover from offline gaps.
  useEffect(() => {
    const onOnline = () => {
      void flushNow();
    };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
    // flushNow is stable below — defined once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce + threshold-based flush. Fires every time the outbox
  // length changes.
  useEffect(() => {
    if (enqueueCount === 0) return;

    if (enqueueCount >= BATCH_THRESHOLD) {
      // Threshold exceeded — flush ASAP without waiting for the debounce.
      cancelDebounce();
      void flushNow();
      return;
    }

    cancelDebounce();
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      void flushNow();
    }, DEBOUNCE_MS);
    return cancelDebounce;
    // flushNow stable. enqueueCount changes drive scheduling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enqueueCount]);

  function cancelDebounce(): void {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  }

  async function flushNow(): Promise<void> {
    if (inFlightRef.current) return;
    const store = useActivityStore.getState();
    if (store.outbox.length === 0) return;
    if (store.isFlushing) return;

    inFlightRef.current = true;
    store.setFlushing(true);
    const batch = store.outbox.slice(0, MAX_BATCH_EVENTS);
    try {
      const result = await flushOutbox(batch);
      useActivityStore.getState().ack(result.ackedIds);
      failedAttemptsRef.current = 0;
      // If more events sit in the queue, schedule a follow-up flush.
      if (useActivityStore.getState().outbox.length > 0) {
        // setTimeout 0 avoids stack-recursing through ack.
        setTimeout(() => void flushNow(), 0);
      }
    } catch {
      // Failure: keep batch in the outbox and back off.
      const attempt = Math.min(failedAttemptsRef.current, BACKOFF_MS.length - 1);
      const delay = BACKOFF_MS[attempt]!;
      failedAttemptsRef.current += 1;
      setTimeout(() => void flushNow(), delay);
    } finally {
      inFlightRef.current = false;
      useActivityStore.getState().setFlushing(false);
    }
  }

  // No render output.
  void isFlushing;
  return null;
}
